import React, { Component } from 'react';
import { isEqual, uniqBy } from 'lodash';
import PropTypes from 'prop-types';
import DefaultTable from 'components/atoms/tables/smarttables/DefaultTable';
import ExportableTable from 'containers/tables/ExportableTable';
import 'react-table-6/react-table.css';
import ContentTile from 'components/atoms/layout/ContentTile';
import ContentHeader from 'components/atoms/layout/ContentHeader';
import CellDiv from 'components/molecules/tables/components/CellDiv';
import RenderingTile from 'components/molecules/RenderingTile';
import helpers from 'helpers';
import Pagination, { RowsPerPageDefault } from 'components/molecules/tables/components/Pagination';
import { insertBeforeItem, insertAfterItem } from 'helpers/utilities';
import { generateFooterData, generateHeaders, applyDefaultColumns } from 'helpers/tables';
import { capitalizeFirstLetter } from 'helpers/strings';

const DEFAULT_ACCESSORS = ['label', 'size', 'impressions', 'ecpa_activities', 'clicks', 'ctr', 'ecpa', 'spend'];

class CreativesPivotTable extends Component {
    constructor(props) {
        super(props);

        let columns;
        let data;
        let uniqueCreativeLabels;

        if (props.hasCreativesData) {
            const table = CreativesPivotTable.createTableData(props.creativesData, props);
            columns = table.columns;
            data = table.data;

            uniqueCreativeLabels = new Set(data.map(c => c.creativeLabel)).size;
        }

        this.state = {
            data,
            columns,
            uniqueCreativeLabels,
            hasCreativesData: props.hasCreativesData,
            showTotalActivities: props.showTotalActivities,
            exportName: props.exportName,
            creativesParams: (this.props.creativesParams || {}),
            smartTable: this.props.smartTable,
            cacheKey: this.props.cacheKey
        };
    }

    shouldComponentUpdate(nextProps) {
        return !isEqual(nextProps, this.props);
    }

    static createTableData(data, props) {
        const initalObj = props.showPlatform ? {
            platform: {
                Header: 'Platform',
                accessor: 'platform',
                Cell: row => <CellDiv value={capitalizeFirstLetter(row.value)} />,
                Footer: (
                    <CellDiv value='' />
                ),
                filter: 'textColumnFilter',
                isIndexCol: false
            }
        } : {};
        const { creativeNameOverride = 'Creative', labelCodeOverride = null } = props;
        const headers = generateHeaders({
            keys: [
                'impressions', 'total_activities', 'ecpa_activities', 'spend', 'clicks',
                'ctr', 'ecpa', 'startedPlays', 'completed_plays', 'completion_rate', 'cpcv', 'conversion_revenue', 'spend',
                'first_quartile_completions', 'midpoint', 'third_quartile_completions'
            ],
            colOptions: {
                labelCode: props.entityObjectiveLabelCodes || labelCodeOverride,
                metricPrefix: props.channel === 'social' ? 'VIDEO ' : null
            },
            footerData: generateFooterData({ data, filteredKeys: ['placementName', 'targetingTacticName'] }),
            initial: {
                ...initalObj,
                label: {
                    Header: `${creativeNameOverride.toUpperCase()} LABEL`,
                    accessor: 'creativeLabel',
                    type: 'string',
                    minWidth: 140,
                    PivotValue: ({ value }) => <span>{value}</span>,
                    isIndexCol: true
                },
                size: {
                    Header: `${creativeNameOverride.toUpperCase()} SIZE`,
                    accessor: 'creativeSize',
                    type: 'string',
                    Aggregated: (row) => {
                        const count = row.subRows.length;
                        let value = `${count} ${creativeNameOverride} Sizes`;
                        if (count === 1) {
                            value = row.subRows[0].creativeSize;
                        } else if (row.isExpanded) {
                            value = '';
                        }
                        return (
                            <CellDiv
                                strong={count !== 1}
                                value={value}
                                style={{ textAlign: 'right' }}
                            />
                        );
                    },
                    Cell: row => <CellDiv value={row.value} style={{ textAlign: 'right' }} />,
                },
            }
        });
        let { accessors = DEFAULT_ACCESSORS } = props;
        const defaultVisableCols = ['creativeLabel', 'impressions', 'ecpa_activities', 'ecpa', 'conversion_revenue'];
        const socialVideoMetrics = ['startedPlays', 'completed_plays', 'first_quartile_completions', 'midpoint', 'third_quartile_completions', 'completion_rate'];
        if (props.showTotalActivities && !accessors.includes('total_activities')) {
            accessors = insertBeforeItem(accessors, 'ecpa_activities', 'total_activities');
        }

        if (props.showConversionRevenue && !accessors.includes('conversion_revenue')) {
            accessors = insertAfterItem(accessors, 'ecpa', 'conversion_revenue');
        }
        if (props.showPlatform && !accessors.includes('platform')) {
            accessors = insertBeforeItem(accessors, 'label', 'platform');
        }

        if (props.channel === 'social' && !socialVideoMetrics.every(m => accessors.includes(m))) {
            accessors.push(...socialVideoMetrics);
        }
        if (props.channel === 'social' && !defaultVisableCols.includes('started_plays')) {
            defaultVisableCols.push('started_plays');
        }

        const columns = applyDefaultColumns(
            accessors.map(accessor => headers[accessor]),
            defaultVisableCols
        );

        return { data, columns };
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        const nextCreativesData = nextProps.creativesData;
        const nextCreativesParams = nextProps.creativesParams;
        const nextExportName = nextProps.exportName;
        const nextHasCreativesData = nextProps.hasCreativesData;
        const nextShowTotalActivities = nextProps.showTotalActivities;
        let nextState = null;

        if (nextCreativesParams !== prevState.creativesParams
            || nextCreativesData !== prevState.creativesData
            || nextShowTotalActivities !== prevState.showTotalActivities
        ) {
            let columns;
            let data;
            let uniqueCreativeLabels;

            if (nextHasCreativesData) {
                const table = CreativesPivotTable.createTableData(nextCreativesData, nextProps);
                columns = table.columns;
                data = table.data;

                uniqueCreativeLabels = new Set(data.map(c => c.creativeLabel)).size;
            }
            // this was added to prevent pivot on tables that do not have pivot data.
            let shouldPivot = false;
            if (Array.isArray(data)) {
                shouldPivot = uniqBy(data, 'creativeLabel').length !== data.length;
            }
            nextState = {
                ...prevState,
                columns,
                data,
                nameOverride: nextProps.creativeNameOverride || 'Creative',
                shouldPivot,
                exportName: nextExportName,
                showTotalActivities: nextShowTotalActivities,
                hasCreativesData: nextHasCreativesData,
                creativesParams: nextCreativesParams,
                uniqueCreativeLabels
            };
        }
        return nextState;
    }

    render() {
        const {
            data,
            exportName,
            shouldPivot,
            uniqueCreativeLabels,
            columns,
            hasCreativesData,
            nameOverride = 'Creative',
            smartTable,
            cacheKey
        } = this.state;
        const expanded = uniqueCreativeLabels > 1 ? undefined : { 0: true };
        const returnTable = (returnSmartTable, tableCacheKey) => {
            if (returnSmartTable) {
                return (
                    <ContentTile padding={helpers.contentPadding('smarttable')}>
                        <ContentHeader type={'table'} allowExport={false}>{`${nameOverride.toUpperCase()} PERFORMANCE`}</ContentHeader>
                        <DefaultTable
                            columns={columns}
                            data={data}
                            cacheKey={tableCacheKey}
                            globalSearchPlaceholder='Search'
                            globalSearch={false}
                            exportData
                            title={`${nameOverride.toUpperCase()} PERFORMANCE`}
                            fileName={exportName}
                            exportName={exportName}
                            pagination={uniqueCreativeLabels > RowsPerPageDefault}
                            aggregate={true}
                            defaultState={{
                                pageSize: RowsPerPageDefault,
                                sortBy: [
                                    { id: 'creativeLabel', desc: false }
                                ]
                            }}
                        />
                    </ContentTile>
                );
            }
            return (<ContentTile padding={helpers.contentPadding('table')}>
                <ContentHeader tableData={data} exportName={exportName} type={'table'}>{`${nameOverride.toUpperCase()} PERFORMANCE`}</ContentHeader>
                <ExportableTable
                    {...(shouldPivot ? { pivotBy: ['creativeLabel'] } : {})}
                    key={1}
                    title={`${nameOverride.toUpperCase()} PERFORMANCE`}
                    exportName={exportName}
                    data={data}
                    columns={columns}
                    expanded={expanded}
                    getTheadThProps={() => ({ style: { outline: 0 } })}
                    getTfootProps={() => ({ style: { backgroundColor: '#E2E2E2', fontWeight: 'bold', boxShadow: 'none' } })}
                    defaultSorted={[{ id: 'creativeLabel', desc: false }, { id: 'creativeSize', desc: false }]}
                    showPagination={uniqueCreativeLabels > RowsPerPageDefault}
                    PaginationComponent={Pagination}
                    defaultPageSize={RowsPerPageDefault}
                    exportRows={data.length}
                    minRows={uniqueCreativeLabels < RowsPerPageDefault ? uniqueCreativeLabels : RowsPerPageDefault}
                    className="-highlight"
                />
            </ContentTile>);
        };

        return hasCreativesData ? returnTable(smartTable, cacheKey) : (
            <RenderingTile height='400px' />
        );
    }
}

CreativesPivotTable.propTypes = {
    exportName: PropTypes.string.isRequired,
    entityObjectiveLabelCode: PropTypes.string,
    creativesData: PropTypes.arrayOf(PropTypes.shape({
        creativeLabel: PropTypes.string,
        creativeSize: PropTypes.string
    })),
    accessors: PropTypes.arrayOf(PropTypes.string),
    hasCreativesData: PropTypes.bool.isRequired
};

export default CreativesPivotTable;



// WEBPACK FOOTER //
// ./src/components/molecules/tables/CreativesPivotTable.js